// ============================================================================
// CadresOverview (F-660) — synthese remuneration des cadres par niveau (GC-D)
// ============================================================================
// Port FIDELE du PoC components/salary/cadres-overview.tsx (279 L @ SHA 7e40e4a).
// 4 tuiles agregees (dont ventilation masse Salaire/Boni*/Avantages + « * possible
// plein boni ») puis 1 carte cliquable par cadre → son profil remuneration, avec
// salaire + croissance 1 an / 3 ans (% et $, teintes), boni reel + cible italique*,
// avantages, remuneration totale, pied classe·echelon / HG.
//
// TRANSPOSITION (PLAN-ATTAQUE §GC-D, P-CE4/P-CE6) — strictement :
//  - StatCard PoC (props size/tone/variances) → tuiles locales (GS_COMPONENTS.card
//    + sous-lignes de variance rendues inline — le StatCard local employee/ui n'a
//    pas de `variances`) ; Badge/EmptyState re-skin @gs/ui + tokens (Regle #28) ;
//  - Intl fr-CA en dur (formatCurrency/Percent) → helpers locale-aware (format.ts,
//    P-CE18) ; libelles FR → cles i18n FR+EN ;
//  - lien profil : `?tab=remuneration&from=grille` (PoC) → `?tab=gestion&from=grille`
//    (parc gest — resorption PDR-EQU-04 : resolveProfileBack('grille') re-ouvert) ;
//  - PII nominative LEGITIME (surface CE admin). Regle #4 : zero emoji.

import { Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { EmptyState, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { Badge } from '../employee/ui';
import { formatCad, formatSignedCad, formatSignedPct, formatPctPlain } from './format';
import type { GridCeCadreAggregates, GridCeCadreSummary } from '../../hooks/useGrilleCe';

// Teinte texte d'une variance/croissance selon le signe (positif emeraude, negatif
// rouge, indetermine slate). Tokens nommes, dark-aware (Regle #28).
function toneClass(n: number | null): string {
  if (n === null) return 'text-slate-500 dark:text-slate-400';
  return n >= 0
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-red-600 dark:text-red-400';
}

export interface CadresOverviewProps {
  summaries: GridCeCadreSummary[];
  aggregates: GridCeCadreAggregates;
  year: number;
  niveau: 1 | 2;
}

export function CadresOverview({ summaries, aggregates, year, niveau }: CadresOverviewProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  if (summaries.length === 0) {
    return (
      <EmptyState
        icon={<Users size={48} aria-hidden="true" />}
        title={t('gest:grilleCe.cadres.empty.title', { niveau })}
        description={t('gest:grilleCe.cadres.empty.description', { niveau })}
      />
    );
  }

  // « +5,4 % · +3 690 $ » (— pour une valeur absente).
  const fmtGrowth = (pct: number | null, amt: number | null): string =>
    `${pct !== null ? formatSignedPct(pct, locale) : '—'} · ${amt !== null ? formatSignedCad(amt, locale) : '—'}`;

  const a = aggregates;

  return (
    <div className="flex flex-col gap-4">
      {/* KPI agreges — vue d'ensemble des cadres du niveau. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <AggCard
          label={t('gest:grilleCe.cadres.kpi.count')}
          value={String(a.count)}
          hint={t('gest:grilleCe.cadres.kpi.positioned', { n: a.configuredCount })}
        />
        <AggCard
          tone="brand"
          label={t('gest:grilleCe.cadres.kpi.avgAug')}
          value={a.avgAugmentationPct !== null ? formatSignedPct(a.avgAugmentationPct, locale) : '—'}
          hint={
            a.avgAug1yAmount !== null
              ? t('gest:grilleCe.cadres.kpi.avgAugHintAmount', { amount: formatSignedCad(a.avgAug1yAmount, locale) })
              : t('gest:grilleCe.cadres.kpi.avgAugHintYears', { year, prevYear: a.prevYear })
          }
          variances={
            a.avgCumulAugmentationPct !== null || a.avgAug3yAmount !== null
              ? [{
                  label: t('gest:grilleCe.cadres.var.3y'),
                  value: fmtGrowth(a.avgCumulAugmentationPct, a.avgAug3yAmount),
                  tone: a.avgCumulAugmentationPct,
                }]
              : undefined
          }
        />
        <AggCard
          label={t('gest:grilleCe.cadres.kpi.masse')}
          value={formatCad(a.masseTotale, locale)}
          variances={masseVariances(a, locale, t)}
        />
        <AggCard
          tone="success"
          label={t('gest:grilleCe.cadres.kpi.remMoyenne')}
          value={a.remMoyenne !== null ? formatCad(a.remMoyenne, locale) : '—'}
          hint={t('gest:grilleCe.cadres.kpi.remHint')}
          variances={growthVariances(a, t, fmtGrowth)}
        />
      </div>

      {/* Une carte cliquable par cadre → son profil remuneration (onglet gestion). */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {summaries.map((s) => (
          <CadreCard key={s.email} summary={s} locale={locale} t={t} />
        ))}
      </div>
    </div>
  );
}

// ─── Types de variance ────────────────────────────────────────────────────────

interface Variance {
  label: string;
  value: React.ReactNode;
  /** Nombre pilotant la teinte (null = neutre). */
  tone: number | null;
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;

// Deux lignes de variance salaire — « 1 an » et « 3 ans », chacune « % · $ ».
function growthVariances(
  a: GridCeCadreAggregates,
  t: TFn,
  fmtGrowth: (pct: number | null, amt: number | null) => string,
): Variance[] | undefined {
  const rows: Variance[] = [];
  if (a.avgAugmentationPct !== null || a.avgAug1yAmount !== null) {
    rows.push({ label: t('gest:grilleCe.cadres.var.1y'), value: fmtGrowth(a.avgAugmentationPct, a.avgAug1yAmount), tone: a.avgAugmentationPct ?? a.avgAug1yAmount });
  }
  if (a.avgCumulAugmentationPct !== null || a.avgAug3yAmount !== null) {
    rows.push({ label: t('gest:grilleCe.cadres.var.3y'), value: fmtGrowth(a.avgCumulAugmentationPct, a.avgAug3yAmount), tone: a.avgCumulAugmentationPct ?? a.avgAug3yAmount });
  }
  return rows.length > 0 ? rows : undefined;
}

// Ventilation de la carte Masse salariale : Σ Salaire / Boni (cible italique*) /
// Avantages, + une ligne « * possible plein boni » = masse au plein boni cible.
function masseVariances(a: GridCeCadreAggregates, locale: string, t: TFn): Variance[] {
  const rows: Variance[] = [
    { label: t('gest:grilleCe.cadres.var.salary'), value: formatCad(a.sumSalaire, locale), tone: null },
    {
      label: t('gest:grilleCe.cadres.var.boni'),
      value: (
        <span className="flex items-center gap-2">
          {a.sumBoniCible > 0 ? <span className="italic">*{formatCad(a.sumBoniCible, locale)}</span> : null}
          <span>{formatCad(a.sumBoni, locale)}</span>
        </span>
      ),
      tone: null,
    },
    { label: t('gest:grilleCe.cadres.var.avantages'), value: formatCad(a.sumAvantages, locale), tone: null },
  ];
  if (a.sumBoniCible > 0) {
    rows.push({
      label: t('gest:grilleCe.cadres.var.fullBoni'),
      value: <span className="italic">{formatCad(a.masseTotale + a.sumBoniCible - a.sumBoni, locale)}</span>,
      tone: null,
    });
  }
  return rows;
}

// ─── Tuile agregee (label, valeur, hint, variances) ──────────────────────────

function AggCard({
  label,
  value,
  hint,
  tone,
  variances,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'brand' | 'success';
  variances?: Variance[];
}) {
  const valueTone =
    tone === 'brand'
      ? 'text-brand-600 dark:text-brand-400'
      : tone === 'success'
        ? 'text-emerald-700 dark:text-emerald-400'
        : 'text-slate-800 dark:text-slate-100';
  return (
    <div className={`${GS_COMPONENTS.card} flex flex-col gap-1 p-3`}>
      <p className="text-chip font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`text-head font-bold tabular-nums ${valueTone}`}>{value}</p>
      {hint ? <p className="text-meta text-slate-500 dark:text-slate-400">{hint}</p> : null}
      {variances && variances.length > 0 ? (
        <div className="mt-1 flex flex-col gap-0.5">
          {variances.map((v, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-meta">
              <span className="text-slate-500 dark:text-slate-400">{v.label}</span>
              <span className={`font-medium tabular-nums ${toneClass(v.tone)}`}>{v.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ─── Ligne « libelle … valeur » (ventilation carte cadre) ────────────────────

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={strong ? 'font-medium text-slate-800 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}>
        {label}
      </span>
      <span className={`tabular-nums ${strong ? 'text-body font-semibold text-slate-800 dark:text-slate-100' : 'font-medium text-slate-700 dark:text-slate-200'}`}>
        {value}
      </span>
    </div>
  );
}

// Sous-ligne de croissance du salaire (« 1 an » / « 3 ans ») : % puis $, teintes.
function GrowthRow({ label, pct, amt, locale }: { label: string; pct: number | null; amt: number | null; locale: string }) {
  if (pct === null && amt === null) return null;
  return (
    <div className="flex items-center justify-between gap-3 text-meta">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className={`flex gap-2 font-medium tabular-nums ${toneClass(pct ?? amt)}`}>
        <span>{pct !== null ? formatSignedPct(pct, locale) : '—'}</span>
        <span>{amt !== null ? formatSignedCad(amt, locale) : '—'}</span>
      </span>
    </div>
  );
}

// ─── Carte cadre (cliquable → profil remuneration) ───────────────────────────

function CadreCard({
  summary: s,
  locale,
  t,
}: {
  summary: GridCeCadreSummary;
  locale: string;
  t: TFn;
}) {
  return (
    <Link
      to={`/profile/${encodeURIComponent(s.email)}?tab=gestion&from=grille`}
      className={`${GS_COMPONENTS.card} flex touch-manipulation flex-col gap-2 p-4 transition-colors hover:border-brand-300 dark:hover:border-brand-500/60`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h3 className="truncate text-body font-semibold tracking-tight text-slate-800 dark:text-slate-100">{s.name}</h3>
          <p className="truncate text-field text-slate-500 dark:text-slate-400">
            {s.jobTitle ?? s.teamName ?? t('gest:grilleCe.cadres.card.defaultRole')}
          </p>
        </div>
        {s.niveauLabel ? <Badge tone="neutral">{s.niveauLabel}</Badge> : null}
      </div>

      {s.configured && s.total !== null ? (
        <>
          <div className="flex flex-col gap-1 text-field">
            <Row label={t('gest:grilleCe.cadres.card.salary')} value={formatCad(s.salaire ?? 0, locale)} />
            <div className="flex flex-col gap-0.5">
              <GrowthRow label={t('gest:grilleCe.cadres.var.1y')} pct={s.augmentationPct} amt={s.aug1yAmount} locale={locale} />
              <GrowthRow label={t('gest:grilleCe.cadres.var.3y')} pct={s.cumulAugmentationPct} amt={s.aug3yAmount} locale={locale} />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-slate-500 dark:text-slate-400">{t('gest:grilleCe.cadres.card.boni')}</span>
              <span className="flex items-center gap-2 tabular-nums">
                {s.boniCible !== null && s.boniCible > 0 ? (
                  <span className="text-meta italic text-slate-500 dark:text-slate-400">
                    *{s.boniCiblePct !== null ? `${formatPctPlain(s.boniCiblePct, locale)} · ` : ''}
                    {formatCad(s.boniCible, locale)}
                  </span>
                ) : null}
                <span className="font-medium text-slate-700 dark:text-slate-200">{formatCad(s.boni ?? 0, locale)}</span>
              </span>
            </div>
            <Row label={t('gest:grilleCe.cadres.card.avantages')} value={formatCad(s.avantages ?? 0, locale)} />
            <div className="my-0.5 border-t border-slate-200 dark:border-slate-700" />
            <Row label={t('gest:grilleCe.cadres.card.total')} value={formatCad(s.total, locale)} strong />
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-meta text-slate-500 dark:text-slate-400">
              {s.classLabel} ·{' '}
              {s.offGrid
                ? t('gest:grilleCe.cadres.card.offGrid')
                : t('gest:grilleCe.cadres.card.echelon', { echelon: s.echelon, max: s.maxEchelon })}
            </p>
            {s.boniCible !== null && s.boniCible > 0 ? (
              <p className="text-meta italic text-slate-500 dark:text-slate-400">
                {t('gest:grilleCe.cadres.card.fullBoni')}{' '}
                <span className="font-medium not-italic tabular-nums text-slate-800 dark:text-slate-100">
                  {formatCad(s.total + s.boniCible - (s.boni ?? 0), locale)}
                </span>
              </p>
            ) : null}
          </div>
        </>
      ) : (
        <p className="text-field text-slate-500 dark:text-slate-400">{t('gest:grilleCe.cadres.card.notConfigured')}</p>
      )}
    </Link>
  );
}
